import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FaArrowRight } from "react-icons/fa";
import ProjectSlider from "./ProjectSlider";
import heroImage from "../assets/Hero-image.png";
import aboutImage from "../assets/image2.png";
import Footer from "./Footer";
import { Link, useNavigate } from "react-router-dom";
import { useLanguage } from "../LanguageContext";
import { translations } from "../translations";

// Full-page Loader
const Loader = () => {
  return (
    <div className="flex justify-center items-center h-screen">
      <div className="w-12 h-12 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
    </div>
  );
};

// Image-specific Loader Component
const ImageLoader = ({ src, alt, className }) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <div className="relative">
      {!loaded && (
        <div className="absolute inset-0 flex justify-center items-center">
          <div className="w-8 h-8 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={`${className} ${
          loaded ? "opacity-100" : "opacity-0"
        } transition-opacity duration-300`}
        loading="lazy" // Native lazy loading
        onLoad={() => setLoaded(true)}
      />
    </div>
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1000); // Adjust timeout as needed
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="w-full">
      {loading ? (
        <Loader />
      ) : (
        <>
          {/* Hero Section */}
          <section
            className="relative h-screen bg-cover bg-center mt-[55px] lg:mt-14 flex items-center text-white"
            style={{ backgroundImage: `url(${heroImage})` }}
          >
            <div className="absolute inset-0 bg-black/50"></div>
            <div className="relative z-10 text-center max-w-4xl mx-auto px-6">
              <motion.h1
                className="text-4xl lg:text-5xl font-bold mb-6"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1 }}
              >
                {translations[language].ThanlyinIndustrialAndCommercialPark}
              </motion.h1>
              <motion.p
                className="text-lg lg:text-xl mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.5 }}
              >
                {translations[language].HeroSectionContent}
              </motion.p>
              <motion.button
                onClick={() => navigate("/project")}
                className="bg-red-600 hover:bg-red-700 px-6 py-3 rounded-lg text-lg font-semibold flex items-center justify-center gap-2 w-fit mx-auto"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 1 }}
              >
                {translations[language].ViewProject} <FaArrowRight />
              </motion.button>
            </div>
          </section>

          {/* About Section */}
          <section className="py-16 px-6 lg:px-12 max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-12">
            <ImageLoader
              src={aboutImage}
              alt="About Us"
              className="w-full lg:w-1/2 rounded-lg shadow-lg"
            />
            <div className="lg:w-1/2">
              <h2 className="text-3xl text-black lg:text-4xl font-bold mb-4">
                {translations[language].AboutMCBWI}
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                {translations[language].AboutMCBWIParagraph}
              </p>
              {/* <a
                href="#contact"
                className="text-red-600 hover:text-red-800 font-semibold text-lg"
              >
                {translations[language].LearnMore} →
              </a> */}
              <Link
                to="/about-us"
                className="text-red-600 hover:text-red-800 font-semibold text-lg"
              >
                {translations[language].LearnMore} →
              </Link>
            </div>
          </section>

          {/* Project Showcase */}
          <section id="projects" className="py-16 bg-gray-100">
            <div className="max-w-6xl mx-auto px-6">
              <h2 className="text-black text-3xl lg:text-4xl font-bold text-center mb-8">
                {translations[language].OurProjects}
              </h2>
              <ProjectSlider />
            </div>
          </section>

          <Footer />
        </>
      )}
    </div>
  );
};

export default HomePage;
